import { Icon } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { chevronDown, close, search } from '@wordpress/icons';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import type { SelectControlProps } from './types';

/**
 * Reusable SelectControl component for WordPress blocks
 *
 * @param props - SelectControl props
 * @returns JSX.Element
 */
export const SelectControl: React.FC<SelectControlProps> = ({
	value,
	onChange,
	label,
	help,
	options,
	placeholder = __('Select an option...', 'redfox-thank-you'),
	multiple = false,
	disabled = false,
	className = '',
	searchable = false,
	style,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [filteredOptions, setFilteredOptions] = useState(options);
	const [showAbove, setShowAbove] = useState(false);
	const [dropdownMaxHeight, setDropdownMaxHeight] = useState<string>('400px');
	const dropdownRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const controlRef = useRef<HTMLDivElement>(null);
	const optionsRef = useRef<HTMLDivElement>(null);

	// Handle click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
				setSearchTerm('');
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Reset search term when dropdown closes
	useEffect(() => {
		if (!isOpen) {
			setSearchTerm('');
		}
	}, [isOpen]);

	// Filter options based on search term
	useEffect(() => {
		if (searchable && searchTerm) {
			const filtered = options.filter((option) =>
				option.label.toLowerCase().includes(searchTerm.toLowerCase()),
			);
			setFilteredOptions(filtered);
		} else {
			setFilteredOptions(options);
		}
	}, [searchTerm, options, searchable]);

	// Focus search input when dropdown opens
	useEffect(() => {
		if (isOpen && searchable && searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [isOpen, searchable]);

	// Calculate dropdown position and max height
	const calculateDropdownPosition = () => {
		if (isOpen && controlRef.current) {
			const controlRect = controlRef.current.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const spaceBelow = viewportHeight - controlRect.bottom - 40; // 40px padding from viewport edge
			const spaceAbove = controlRect.top - 140; // 100px padding from viewport edge

			// Calculate the actual height needed for content
			const searchHeight = searchable ? 48 : 0;
			const itemHeight = 40;
			const padding = 16;
			const idealHeight = filteredOptions.length * itemHeight + searchHeight + padding;

			// Determine if dropdown should show above or below
			const shouldShowAbove = spaceBelow < 200 && spaceAbove > spaceBelow;
			setShowAbove(shouldShowAbove);

			// Calculate max height based on available space
			const availableSpace = shouldShowAbove ? spaceAbove : spaceBelow;
			const maxHeight = Math.max(150, Math.min(availableSpace, idealHeight));
			setDropdownMaxHeight(`${maxHeight}px`);
		}
	};

	// Calculate dropdown position when opening or options change
	useEffect(() => {
		calculateDropdownPosition();
	}, [isOpen, filteredOptions.length, searchable]);

	// Recalculate dropdown position on window resize
	useEffect(() => {
		if (!isOpen) return;

		const handleResize = () => {
			calculateDropdownPosition();
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [isOpen, filteredOptions.length, searchable]);

	// Scroll selected option into view when opening
	useEffect(() => {
		if (isOpen) {
			const container = optionsRef.current;
			const selectedEl = container?.querySelector('[aria-selected="true"]') as HTMLElement | null;
			selectedEl?.scrollIntoView({ block: 'nearest' });
		}
	}, [isOpen, filteredOptions]);

	/**
	 * Get display text for selected value(s)
	 */
	const getDisplayText = (): string => {
		if (multiple) {
			const selectedValues = Array.isArray(value) ? value : [value];
			if (selectedValues.length === 0) return placeholder;

			const selectedLabels = selectedValues
				.map(
					(val) =>
						options.find((opt) => opt.value === val)?.label,
				)
				.filter(Boolean);

			if (selectedLabels.length === 1) {
				return selectedLabels[0] ?? '';
			}
			if (selectedLabels.length === 2) {
				return selectedLabels.join(', ');
			}
			return `${selectedLabels.length} ${__(
				'items selected',
				'redfox-thank-you',
			)}`;
		} else {
			const selectedOption = options.find(
				(opt) => opt.value === value,
			);
			return selectedOption?.label ?? placeholder;
		}
	};

	/**
	 * Handle option selection
	 */
	const handleOptionSelect = (optionValue: string | number) => {
		if (multiple) {
			const currentValues = Array.isArray(value) ? value : [];
			const newValues = currentValues.includes(optionValue)
				? currentValues.filter((val) => val !== optionValue)
				: [...currentValues, optionValue];
			onChange(newValues);
		} else {
			onChange(optionValue);
			setIsOpen(false);
			setSearchTerm('');
		}
	};

	/**
	 * Check if option is selected
	 */
	const isOptionSelected = (optionValue: string | number): boolean => {
		if (multiple) {
			const currentValues = Array.isArray(value) ? value : [];
			return currentValues.includes(optionValue);
		}
		return value === optionValue;
	};

	/**
	 * Handle clear selection
	 */
	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		onChange(multiple ? [] : '');
	};

	/**
	 * Handle keyboard navigation
	 */
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (disabled) return;

		switch (e.key) {
			case 'Enter':
			case ' ':
				if (!isOpen) {
					e.preventDefault();
					setIsOpen(true);
				}
				break;
			case 'Escape':
				setIsOpen(false);
				setSearchTerm('');
				break;
			case 'ArrowDown':
				if (!isOpen) {
					e.preventDefault();
					setIsOpen(true);
				}
				break;
		}
	};

	const baseClassName = 'redfox-thank-you-select-control';
	const wrapperClasses = classNames(baseClassName, className, {
		[`${baseClassName}--open`]: isOpen,
		[`${baseClassName}--disabled`]: disabled,
		[`${baseClassName}--multiple`]: multiple,
		[`${baseClassName}--above`]: showAbove,
	});

	const hasValue = multiple
		? Array.isArray(value) && value.length > 0
		: value !== '' && value !== null && value !== undefined;

	return (
		<div className={wrapperClasses} style={style}>
			{label && (
				<label className={`${baseClassName}__label`}>
					{label}
				</label>
			)}

			<div
				className={`${baseClassName}__wrapper`}
				ref={dropdownRef}
			>
				<div
					className={`${baseClassName}__control`}
					ref={controlRef}
					onClick={() => !disabled && setIsOpen(!isOpen)}
					onKeyDown={handleKeyDown}
					tabIndex={disabled ? -1 : 0}
					role="combobox"
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					aria-label={label}
				>
					<span className={`${baseClassName}__value`}>
						{getDisplayText()}
					</span>

					<div className={`${baseClassName}__indicators`}>
						{hasValue && !disabled && (
							<button
								className={`${baseClassName}__clear`}
								onClick={handleClear}
								type="button"
								aria-label={__(
									'Clear selection',
									'redfox-thank-you',
								)}
							>
								<Icon icon={close} size={16} />
							</button>
						)}
						<div
							className={`${baseClassName}__dropdown-indicator`}
						>
							<Icon icon={chevronDown} size={20} />
						</div>
					</div>
				</div>

				{isOpen && (
					<div
						className={`${baseClassName}__menu`}
						style={{ maxHeight: dropdownMaxHeight }}
					>
						{searchable && (
							<div className={`${baseClassName}__search`}>
								<Icon icon={search} size={16} />
								<input
									ref={searchInputRef}
									type="text"
									className={`${baseClassName}__search-input`}
									placeholder={__(
										'Search options...',
										'redfox-thank-you',
									)}
									value={searchTerm}
									onChange={(e) =>
										setSearchTerm(e.target.value)
									}
								/>
							</div>
						)}

						<div
							ref={optionsRef}
							className={`${baseClassName}__options`}
							role="listbox"
							aria-multiselectable={multiple}
							style={{
								maxHeight: searchable
									? `calc(${dropdownMaxHeight} - 48px)`
									: dropdownMaxHeight,
							}}
						>
							{filteredOptions.length === 0 ? (
								<div
									className={`${baseClassName}__no-options`}
								>
									{searchTerm
										? __(
											'No options found',
											'redfox-thank-you',
										)
										: __(
											'No options available',
											'redfox-thank-you',
										)}
								</div>
							) : (
								filteredOptions.map((option) => (
									<div
										key={option.value}
										className={classNames(
											`${baseClassName}__option`,
											{
												[`${baseClassName}__option--selected`]:
													isOptionSelected(
														option.value,
													),
												[`${baseClassName}__option--disabled`]:
													option.disabled,
											},
										)}
										onClick={() =>
											!option.disabled &&
											handleOptionSelect(option.value)
										}
										role="option"
										aria-selected={isOptionSelected(
											option.value,
										)}
									>
										{multiple && (
											<input
												type="checkbox"
												checked={isOptionSelected(
													option.value,
												)}
												onChange={() => { }} // Handled by onClick
												tabIndex={-1}
												className={`${baseClassName}__checkbox`}
											/>
										)}
										<span
											className={`${baseClassName}__option-text`}
										>
											{option.label}
										</span>
									</div>
								))
							)}
						</div>
					</div>
				)}
			</div>

			{help && <p className={`${baseClassName}__help`}>{help}</p>}
		</div>
	);
};
